import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { api, type AuthSession, type StoreRecord } from './api'
import { AuthProvider, useAuth } from './auth-context'
import { availableNavItems, Header, Sidebar } from './components/layout'
import { Toast } from './components/ui'
import type { View } from './domain'
import { loadSession, saveSession } from './session'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { OrdersPage } from './pages/OrdersPage'
import { StaffPage } from './pages/StaffPage'
import { AttendancePage } from './pages/AttendancePage'
import { PerformancePage } from './pages/PerformancePage'
import { StoresPage } from './pages/StoresPage'
import { RulesPage } from './pages/RulesPage'
import { AuditPage } from './pages/AuditPage'
import { ChangePasswordPage } from './pages/ChangePasswordPage'
import { AccountsPage } from './pages/AccountsPage'

function App() {
  const [session, setSession] = useState<AuthSession | null>(() => loadSession())
  const expired = useCallback(() => { saveSession(null); setSession(null) }, [])
  if (!session) return <LoginPage onAuthenticated={next => { saveSession(next); setSession(next) }} />
  const logout = async () => {
    try { await api.logout(session.refreshToken) } finally { expired() }
  }
  return <AuthProvider session={session} onSession={setSession} onExpired={expired}>{session.user.passwordChangeRequired
    ? <ChangePasswordPage onChanged={next => { saveSession(next); setSession(next) }} onLogout={() => void logout()} />
    : <AuthenticatedApp onLogout={expired} />}</AuthProvider>
}

function AuthenticatedApp({ onLogout }: { onLogout: () => void }) {
  const { authorized, session } = useAuth()
  const [activeView, setActiveView] = useState<View>(() => availableNavItems(session)[0]?.id ?? 'dashboard')
  const [stores, setStores] = useState<StoreRecord[]>([])
  const [storeId, setStoreId] = useState(session.user.storeIds[0] ?? '')
  const [date, setDate] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  })
  const [orderCount, setOrderCount] = useState(0)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [storeError, setStoreError] = useState('')
  const [toast, setToast] = useState('')
  const timer = useRef<number | null>(null)
  const visibleViews = useMemo(() => availableNavItems(session).map(item => item.id), [session])
  const allowedViews = useMemo(() => new Set(visibleViews), [visibleViews])

  const notify = useCallback((message: string) => {
    setToast(message)
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setToast(''), 2600)
  }, [])
  const loadStores = useCallback(async () => {
    setStoreError('')
    try {
      const values = await authorized(token => api.stores(token))
      setStores(values)
      setStoreId(current => values.some(item => item.id === current) ? current : values[0]?.id ?? '')
    } catch (reason) { setStoreError(reason instanceof Error ? reason.message : '门店加载失败') }
  }, [authorized])
  useEffect(() => { void loadStores() }, [loadStores])
  useEffect(() => {
    if (!storeId || !allowedViews.has('orders')) { setOrderCount(0); return }
    void authorized(token => api.orders(token, storeId)).then(page => setOrderCount(page.content.filter(item => ['WAITING', 'IN_SERVICE', 'AWAITING_SETTLEMENT'].includes(item.status)).length)).catch(() => setOrderCount(0))
  }, [allowedViews, authorized, storeId])
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current) }, [])
  useEffect(() => { if (!allowedViews.has(activeView) && visibleViews[0]) setActiveView(visibleViews[0]) }, [activeView, allowedViews, visibleViews])

  const logout = async () => {
    try { await api.logout(session.refreshToken) } finally { saveSession(null); onLogout() }
  }
  const navigate = (view: View) => { if (allowedViews.has(view)) setActiveView(view); setMobileNavOpen(false) }
  const common = { storeId, onNotify: notify }
  return <div className="app-shell">
    <Sidebar activeView={activeView} open={mobileNavOpen} orderCount={orderCount} session={session} onNavigate={navigate} onClose={() => setMobileNavOpen(false)} onLogout={() => void logout()} />
    {mobileNavOpen && <button className="sidebar-scrim" aria-label="关闭导航" onClick={() => setMobileNavOpen(false)} />}
    <div className="main-shell">
      <Header activeView={activeView} stores={stores} storeId={storeId} date={date} session={session} onStore={setStoreId} onDate={setDate} onMenu={() => setMobileNavOpen(true)} />
      <main className="content-area">
        {storeError && <div className="api-error global-error"><AlertCircle size={16} />{storeError}</div>}
        {!storeError && !stores.length && !['stores', 'accounts', 'audit'].includes(activeView) && <div className="panel workspace-empty"><AlertCircle size={20} /><b>当前账号没有可访问的门店</b><span>Root 可在“门店与房间”创建第一家门店；其他账号需由管理员授权。</span></div>}
        {storeId && activeView === 'dashboard' && <DashboardPage storeId={storeId} onNavigate={navigate} />}
        {storeId && activeView === 'orders' && <OrdersPage {...common} date={date} onCount={setOrderCount} />}
        {storeId && activeView === 'staff' && <StaffPage {...common} stores={stores} />}
        {storeId && activeView === 'attendance' && <AttendancePage {...common} date={date} />}
        {storeId && activeView === 'performance' && <PerformancePage {...common} date={date} />}
        {activeView === 'stores' && <StoresPage {...common} stores={stores} onStoresChanged={values => { setStores(values); setStoreId(current => values.some(item => item.id === current) ? current : values[0]?.id ?? '') }} />}
        {storeId && activeView === 'rules' && <RulesPage {...common} />}
        {activeView === 'accounts' && <AccountsPage storeId={storeId} stores={stores} onNotify={notify} />}
        {activeView === 'audit' && <AuditPage />}
      </main>
    </div>
    <Toast message={toast} />
  </div>
}

export default App
